import React, { useMemo, useState } from "react";
import { Pizza, Loader2 } from "lucide-react";
import ReactSelect, { SingleValue, StylesConfig } from "react-select";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns-tz";

import PageHeader from "../../../components/PageHeader";
import { SELECT_STYLES } from "../../../components/DateFilters/constants";
import { BrazilDate } from "../../../utils/BrazilDate";
import { lojaService } from "../../../services/lojaService";
import { fetchRelatorioRodizioPizzas } from "../../../services/pedidoIntegracaoService";

type LojaOpt = { value: number; label: string };

const LOJA_SELECT_STYLES = SELECT_STYLES as unknown as StylesConfig<
  LojaOpt,
  false
>;

function getDefaultMonthRange() {
  const now = new Date();
  const tz = BrazilDate.getTimezone();
  const yearStr = format(now, "yyyy", { timeZone: tz });
  const monthStr = format(now, "MM", { timeZone: tz });
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  const firstDay = BrazilDate.create(year, month, 1);
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  const firstDayNextMonth = BrazilDate.create(nextYear, nextMonth, 1);
  const lastDay = new Date(firstDayNextMonth.getTime() - 24 * 60 * 60 * 1000);
  return {
    dataInicio: BrazilDate.formatISO(firstDay),
    dataFim: BrazilDate.formatISO(lastDay),
  };
}

const DEFAULT_MONTH = getDefaultMonthRange();

function formatQty(n: number): string {
  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function tipoLabel(tipo: string | null): string {
  if (tipo === "doce") return "Doce";
  if (tipo === "salgada") return "Salgada";
  return "—";
}

const RodizioPizzas: React.FC = () => {
  const [startDate, setStartDate] = useState(DEFAULT_MONTH.dataInicio);
  const [endDate, setEndDate] = useState(DEFAULT_MONTH.dataFim);
  const [lojaId, setLojaId] = useState<number | null>(null);
  const [applied, setApplied] = useState<null | {
    dataInicio: string;
    dataFim: string;
    lojaId: number | null;
  }>(null);

  const { data: lojas = [] } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
  });

  const lojaOptions = useMemo(
    () => lojas.map((l) => ({ value: l.id, label: l.nome })),
    [lojas]
  );
  const lojaSel = lojaOptions.find((o) => o.value === (lojaId ?? -1)) ?? null;

  const { data, isFetching, refetch } = useQuery({
    queryKey: [
      "relatorio-rodizio-pizzas",
      applied?.dataInicio ?? "no-applied",
      applied?.dataFim ?? "no-applied",
      applied?.lojaId ?? "no-applied",
    ],
    enabled: applied != null,
    queryFn: () =>
      fetchRelatorioRodizioPizzas({
        dataInicio: applied!.dataInicio,
        dataFim: applied!.dataFim,
        lojaId: applied!.lojaId,
      }),
  });

  const handleAplicar = () => {
    setApplied({
      dataInicio: startDate,
      dataFim: endDate,
      lojaId,
    });
  };

  const pIni =
    applied != null
      ? BrazilDate.formatBR(BrazilDate.fromISOString(applied.dataInicio), false)
      : null;
  const pFim =
    applied != null
      ? BrazilDate.formatBR(BrazilDate.fromISOString(applied.dataFim), false)
      : null;
  const lojaLabel =
    applied != null
      ? applied.lojaId != null
        ? (lojaOptions.find((o) => o.value === applied.lojaId)?.label ?? "—")
        : "Todas"
      : null;

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader title="Rodízio de Pizzas" description="" />

      <div className="mb-6 rounded-lg border border-neutral-200 bg-neutral-50/80 p-4">
        <p className="mb-3 text-sm font-medium text-neutral-800">Filtros</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="rodizio-ini"
              className="text-xs font-medium text-neutral-700"
            >
              Data início
            </label>
            <input
              id="rodizio-ini"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="rodizio-fim"
              className="text-xs font-medium text-neutral-700"
            >
              Data fim
            </label>
            <input
              id="rodizio-fim"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-neutral-700">Loja</span>
            <ReactSelect<LojaOpt, false>
              inputId="rodizio-loja"
              value={lojaSel}
              onChange={(o: SingleValue<LojaOpt>) =>
                setLojaId(o?.value ?? null)
              }
              options={lojaOptions}
              isClearable
              placeholder="Todas"
              styles={LOJA_SELECT_STYLES}
              className="text-sm"
              noOptionsMessage={() => "Nenhuma loja"}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={handleAplicar}
          className="mt-4 inline-flex rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90"
        >
          Aplicar filtros
        </button>
      </div>

      {applied != null ? (
        <p className="mb-4 text-sm text-neutral-600">
          Período: <span className="font-medium text-neutral-800">{pIni}</span>{" "}
          a <span className="font-medium text-neutral-800">{pFim}</span>
          {" · "}
          Loja:{" "}
          <span className="font-medium text-neutral-800">{lojaLabel}</span>
          {" · "}
          Total rodízio:{" "}
          <span className="font-medium text-neutral-800">
            {formatQty(data?.totalPizzas ?? 0)}
          </span>
        </p>
      ) : (
        <p className="mb-4 text-sm text-neutral-600">
          Selecione o período e clique em <strong>Aplicar filtros</strong>.
        </p>
      )}

      <div className="relative">
        {applied != null && isFetching && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/60">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {applied != null ? (
          <div className="space-y-8">
            <section aria-label="Resumo por tamanho">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Pizza size={22} strokeWidth={2} />
                </div>
                <div className="min-w-0">
                  <h2 className="text-base font-semibold text-neutral-900">
                    Por tamanho
                  </h2>
                  <p className="text-sm text-neutral-600">
                    Mesma base do indicador «Qtd. Rodízio» do dashboard.
                  </p>
                </div>
              </div>

              {(data?.porTamanho ?? []).length === 0 ? (
                <div className="rounded-lg border border-neutral-200 bg-white px-4 py-8 text-center text-sm text-neutral-500 shadow-sm">
                  Nenhuma pizza de rodízio no período.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-neutral-200 bg-white shadow-sm">
                  <table className="min-w-full text-left text-sm">
                    <thead className="border-b border-neutral-200 bg-neutral-50">
                      <tr>
                        <th className="px-4 py-3 font-semibold text-neutral-800">
                          Tamanho
                        </th>
                        <th className="px-4 py-3 text-right font-semibold text-neutral-800">
                          Qtd. pizzas
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data?.porTamanho ?? []).map((row) => (
                        <tr
                          key={row.tamanhoNome}
                          className="border-b border-neutral-100 last:border-0"
                        >
                          <td className="px-4 py-2.5 text-neutral-900">
                            {row.tamanhoNome}
                          </td>
                          <td className="px-4 py-2.5 text-right font-medium tabular-nums text-neutral-900">
                            {formatQty(row.quantidadePizzas)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t-2 border-neutral-200 bg-neutral-50/90">
                      <tr>
                        <td className="px-4 py-2.5 text-sm font-semibold text-neutral-800">
                          Total
                        </td>
                        <td className="px-4 py-2.5 text-right text-sm font-bold tabular-nums text-neutral-900">
                          {formatQty(data?.totalPizzas ?? 0)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </section>

            <section aria-label="Sabores e tamanhos">
              <div className="mb-4 min-w-0">
                <h2 className="text-base font-semibold text-neutral-900">
                  Por sabor e tamanho
                </h2>
                <p className="text-sm text-neutral-600">
                  Quantidade de pizzas finalizadas de cada sabor no período.
                </p>
              </div>

              {(data?.linhas ?? []).length === 0 ? (
                <div className="rounded-lg border border-neutral-200 bg-white px-4 py-8 text-center text-sm text-neutral-500 shadow-sm">
                  Nenhum sabor encontrado no período.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-neutral-200 bg-white shadow-sm">
                  <table className="min-w-full text-left text-sm">
                    <thead className="border-b border-neutral-200 bg-neutral-50">
                      <tr>
                        <th className="px-4 py-3 font-semibold text-neutral-800">
                          Sabor
                        </th>
                        <th className="px-4 py-3 font-semibold text-neutral-800">
                          Tamanho
                        </th>
                        <th className="px-4 py-3 font-semibold text-neutral-800">
                          Tipo
                        </th>
                        <th className="px-4 py-3 text-right font-semibold text-neutral-800">
                          Qtd. pizzas
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data?.linhas ?? []).map((row) => (
                        <tr
                          key={`${row.saborNome}|${row.tamanhoNome}|${row.tipo ?? ""}`}
                          className="border-b border-neutral-100 last:border-0"
                        >
                          <td className="px-4 py-2.5 text-neutral-900">
                            {row.saborNome}
                          </td>
                          <td className="px-4 py-2.5 text-neutral-900">
                            {row.tamanhoNome}
                          </td>
                          <td className="px-4 py-2.5 text-neutral-700">
                            {tipoLabel(row.tipo)}
                          </td>
                          <td className="px-4 py-2.5 text-right font-medium tabular-nums text-neutral-900">
                            {formatQty(row.quantidadePizzas)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t-2 border-neutral-200 bg-neutral-50/90">
                      <tr>
                        <td
                          colSpan={3}
                          className="px-4 py-2.5 text-sm font-semibold text-neutral-800"
                        >
                          Total
                        </td>
                        <td className="px-4 py-2.5 text-right text-sm font-bold tabular-nums text-neutral-900">
                          {formatQty(data?.totalPizzas ?? 0)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </section>
          </div>
        ) : null}

        {applied != null ? (
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-6 text-sm font-medium text-primary hover:underline"
          >
            Recarregar dados
          </button>
        ) : null}
      </div>
    </main>
  );
};

export default RodizioPizzas;
